import { makeAutoObservable, reaction } from "mobx";

import { disposeApi, recentDocuments, recordDocumentVisit } from "@/api";

import type { DocumentShort } from "@/api";
import type { RootStore } from "@/store/RootStore";

export const RECENT_DOCUMENTS_COLLAPSED_COUNT = 3;

/** The Recent section shows only once the team has this many documents outside the trash. */
export const RECENT_DOCUMENTS_MIN_TEAM_DOCUMENTS = 25;

// Keep in sync with DocumentVisit::RECENT_LIMIT in the API.
export const RECENT_DOCUMENTS_LIMIT = 10;

export class RecentDocumentsStore {
  private rootStore: RootStore;

  private fetchDispose?: () => void;

  private currentTeamReactionDispose?: () => void;

  private visitedUids: string[] = [];

  documents: DocumentShort[] = [];

  isSectionOpen = true;

  isExpanded = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this);

    this.currentTeamReactionDispose = reaction(
      () => this.team?.uid,
      (_teamUid, previousTeamUid) => {
        // The team first resolving (teams just loaded) isn't a switch: nothing
        // team-scoped has been fetched yet.
        if (previousTeamUid === undefined) return;
        this.fetchDispose?.();
        this.fetchDispose = undefined;
        this.documents = [];
        this.visitedUids = [];
        this.isExpanded = false;
        this.isSectionOpen = true;
      },
    );
  }

  get team() {
    return this.rootStore.teams.currentTeam;
  }

  toggleSection() {
    this.isSectionOpen = !this.isSectionOpen;
  }

  expandList() {
    this.isExpanded = true;
  }

  // Visits recorded while the request was in flight may be missing from the response.
  setDocuments(documents: DocumentShort[]) {
    this.documents = documents;
    this.visitedUids.forEach((uid) => this.moveToFront(uid));
  }

  async fetch(teamUid: string, signal?: AbortSignal) {
    try {
      const result = await recentDocuments({ teamUid }, { signal });
      if (signal?.aborted) return;

      this.setDocuments(result.documents ?? []);
    } catch (err) {
      if (signal?.aborted) return;
      console.error("Failed to load the recent documents:", err);
    }
  }

  fetchWithDispose(teamUid: string) {
    this.fetchDispose?.();
    this.fetchDispose = disposeApi((signal) => this.fetch(teamUid, signal));
  }

  recordVisit(docUid: string) {
    this.visitedUids = [...this.visitedUids.filter((uid) => uid !== docUid), docUid].slice(
      -RECENT_DOCUMENTS_LIMIT,
    );
    this.moveToFront(docUid);

    recordDocumentVisit(docUid).catch((err) => {
      console.error("Failed to record the document visit:", err);
    });
  }

  private moveToFront(docUid: string) {
    const index = this.documents.findIndex((doc) => doc.uid === docUid);
    if (index > 0) {
      const [visited] = this.documents.splice(index, 1);
      this.documents.unshift(visited);
    } else if (index === -1) {
      const item = this.rootStore.documentTree.itemById(docUid);
      if (item?.typename === "DocumentShort") this.documents.unshift(item);
    }
    this.documents.splice(RECENT_DOCUMENTS_LIMIT);
  }

  dispose() {
    this.currentTeamReactionDispose?.();
    this.fetchDispose?.();
  }
}
